"use client";

import React from "react";
import type { TabConfig } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Contact_TasksDetail } from "@/lib/types/task-extensions";
import { TaskModalDetailsPrimary } from "@/features/tasks/components/TaskModalDetailsPrimary";
import {
  TaskActivityTimelineTab,
  TaskContactDisplay,
  TaskLinkedCompanyPanel,
  TaskLinkedDealPanel,
  TaskLinkedOfferingPanel,
  TaskLinkedTicketPanel,
  TaskLinkedTimeEntryPanel,
  TaskCompanyDisplay,
  TaskAssigneesDisplay,
} from "@/features/tasks/components/TaskDetailTabComponents";
import {
  getPrimaryLinkedEntityKind,
  linkedEntityTabLabel,
} from "@/lib/utils/crm/task-linked-entity";

// ============================================================================
// TASK DETAIL MODAL TABS HOOK
// ============================================================================

const ACTIVITY_TAB = {
  id: "activity",
  label: "Activity",
  content: { type: "activity" as const },
} as TabConfig<Contact_TasksDetail>;

const CONTACT_TAB = {
  id: "contact",
  label: "Contact",
  content: { type: "custom" as const, customComponent: "TaskContactDisplay" },
} as TabConfig<Contact_TasksDetail>;

const COMPANY_TAB = {
  id: "company",
  label: "Company & contact",
  content: { type: "custom" as const, customComponent: "TaskCompanyDisplay" },
} as TabConfig<Contact_TasksDetail>;

const ASSIGNED_TO_TAB = {
  id: "assigned-to",
  label: "Assigned to",
  content: { type: "custom" as const, customComponent: "TaskAssigneesDisplay" },
} as TabConfig<Contact_TasksDetail>;

const LINKED_TAB_PLACEHOLDER: TabConfig<Contact_TasksDetail> = {
  id: "linked",
  label: "Linked",
  content: { type: "custom" as const, customComponent: "TaskLinkedEntity" },
};

export interface UseTaskDetailModalTabsReturn {
  tabs: TabConfig<Contact_TasksDetail>[];
  renderPrimaryArea: (
    activeTab: string,
    task: Contact_TasksDetail,
    onTagsUpdate?: (newTags: string[]) => Promise<void>,
    onInlineEdit?: (item: Contact_TasksDetail) => Promise<void>
  ) => React.ReactNode;
  renderInfoPanel: (task: Contact_TasksDetail) => React.ReactNode;
}

/**
 * The task modal's tab set is a literal — hoisted to module scope so it is
 * allocated ONCE. Built inside the hook it was a fresh array every render,
 * which invalidated `useDetailModalTabs`'s `entityTabsConfig` memo on every
 * render (MEMO_STABLE_PROPS).
 */
const TASK_DETAIL_TABS: TabConfig<Contact_TasksDetail>[] = [
  {
    id: "details",
    label: "Details",
    icon: "info",
    variant: "blue",
    group: "core",
    priority: 1,
    content: { type: "custom", customComponent: "TaskModalDetailsPrimary" },
  },
  {
    id: "activity",
    label: "Activity",
    icon: "activity",
    variant: "green",
    group: "activity",
    priority: 2,
    content: { type: "custom", customComponent: "TaskActivityTimeline" },
  },
  {
    id: "contact",
    label: "Contact",
    icon: "user",
    variant: "purple",
    group: "related",
    priority: 3,
    visible: (task) => {
      const kind = getPrimaryLinkedEntityKind(task);
      return kind === "contacts" || (!kind && !!task.contact_id);
    },
    content: { type: "custom", customComponent: "TaskContactDisplay" },
  },
  {
    id: "deals",
    label: "Deal",
    icon: "briefcase",
    variant: "purple",
    group: "related",
    priority: 4,
    visible: (task) => getPrimaryLinkedEntityKind(task) === "deals",
    content: { type: "custom", customComponent: "TaskLinkedDealPanel" },
  },
  {
    id: "companies",
    label: "Company",
    icon: "building",
    variant: "purple",
    group: "related",
    priority: 5,
    visible: (task) => getPrimaryLinkedEntityKind(task) === "companies",
    content: { type: "custom", customComponent: "TaskLinkedCompanyPanel" },
  },
  {
    id: "tickets",
    label: "Ticket",
    icon: "ticket",
    variant: "purple",
    group: "related",
    priority: 6,
    visible: (task) => getPrimaryLinkedEntityKind(task) === "tickets",
    content: { type: "custom", customComponent: "TaskLinkedTicketPanel" },
  },
  {
    id: "time_entries",
    label: "Time entry",
    icon: "clock",
    variant: "purple",
    group: "related",
    priority: 7,
    visible: (task) => getPrimaryLinkedEntityKind(task) === "time_entries",
    content: { type: "custom", customComponent: "TaskLinkedTimeEntryPanel" },
  },
  {
    id: "offerings",
    label: "Offering",
    icon: "package",
    variant: "purple",
    group: "related",
    priority: 8,
    visible: (task) => getPrimaryLinkedEntityKind(task) === "offerings",
    content: { type: "custom", customComponent: "TaskLinkedOfferingPanel" },
  },
  // New PR 981 tabs
  {
    id: "company",
    label: "Company & contact",
    icon: "building",
    variant: "red",
    group: "related",
    priority: 9,
    content: { type: "custom", customComponent: "TaskCompanyDisplay" },
  },
  {
    id: "assigned-to",
    label: "Assigned to",
    icon: "users",
    variant: "blue",
    group: "related",
    priority: 10,
    content: { type: "custom", customComponent: "TaskAssigneesDisplay" },
  },
];

export function useTaskDetailModalTabs(): UseTaskDetailModalTabsReturn {
  const tabs = TASK_DETAIL_TABS;


  const renderPrimaryArea = React.useCallback((
    activeTab: string,
    task: Contact_TasksDetail,
    _onTagsUpdate?: (newTags: string[]) => Promise<void>,
    onInlineEdit?: (item: Contact_TasksDetail) => Promise<void>
  ): React.ReactNode => {
    const save = onInlineEdit ?? (async () => {});
    const linkedKind = getPrimaryLinkedEntityKind(task);

    switch (activeTab) {
      case "details":
        return <TaskModalDetailsPrimary task={task} onInlineEdit={save} />;
      case "activity":
        return <TaskActivityTimelineTab item={task} tab={ACTIVITY_TAB} />;
      case "contact":
        return <TaskContactDisplay item={task} tab={CONTACT_TAB} />;
      case "deals":
        return linkedKind === "deals" ? (
          <TaskLinkedDealPanel item={task} tab={LINKED_TAB_PLACEHOLDER} />
        ) : null;
      case "companies":
        return linkedKind === "companies" ? (
          <TaskLinkedCompanyPanel item={task} tab={LINKED_TAB_PLACEHOLDER} />
        ) : null;
      case "tickets":
        return linkedKind === "tickets" ? (
          <TaskLinkedTicketPanel item={task} tab={LINKED_TAB_PLACEHOLDER} />
        ) : null;
      case "time_entries":
        return linkedKind === "time_entries" ? (
          <TaskLinkedTimeEntryPanel item={task} tab={LINKED_TAB_PLACEHOLDER} />
        ) : null;
      case "offerings":
        return linkedKind === "offerings" ? (
          <TaskLinkedOfferingPanel item={task} tab={LINKED_TAB_PLACEHOLDER} />
        ) : null;
      case "company":
        return <TaskCompanyDisplay item={task} tab={COMPANY_TAB} />;
      case "assigned-to":
        return <TaskAssigneesDisplay item={task} tab={ASSIGNED_TO_TAB} />;
      default:
        return <TaskModalDetailsPrimary task={task} onInlineEdit={save} />;
    }
  }, []);

  const renderInfoPanel = React.useCallback((task: Contact_TasksDetail): React.ReactNode => {
    const linked = getPrimaryLinkedEntityKind(task);
    const linkedLabel = linked ? linkedEntityTabLabel(linked) : null;

    return (
      <div className="space-y-4">
        <div className="border rounded-lg p-4 bg-white">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Task information</h3>
          <div className="space-y-2 text-sm">
            {linkedLabel ? (
              <div>
                <span className="text-gray-600">Linked to:</span>
                <p className="text-gray-900">{linkedLabel}</p>
              </div>
            ) : null}
            {task.status && (
              <div>
                <span className="text-gray-600">Status:</span>
                <p className="text-gray-900 capitalize">{String(task.status).replace(/_/g, " ")}</p>
              </div>
            )}
            {task.priority && (
              <div>
                <span className="text-gray-600">Priority:</span>
                <p className="text-gray-900 capitalize">{String(task.priority)}</p>
              </div>
            )}
            {task.due_date && (
              <div>
                <span className="text-gray-600">Due date:</span>
                <p className="text-gray-900">{new Date(task.due_date).toLocaleDateString()}</p>
              </div>
            )}
            {task.assigned_user && (
              <div>
                <span className="text-gray-600">Assigned to:</span>
                <p className="text-gray-900">
                  {task.assigned_user.first_name} {task.assigned_user.last_name}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, []);

  return React.useMemo(
    () => ({
      tabs,
      renderPrimaryArea,
      renderInfoPanel,
    }),
    [tabs, renderPrimaryArea, renderInfoPanel],
  );
}
